import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import SchemaOrg from "@/components/base/SchemaOrg";
import { categories, categoryMap } from "@/mocks/categories";
import { getArticlesByCategory, articleMap } from "@/mocks/articles";
import { useTranslation } from "react-i18next";

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation("category");
  const [activeSlug, setActiveSlug] = useState(slug || "chuanda-gonglue");

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
    }
  }, [slug]);

  const currentCategory = categoryMap[activeSlug];
  const categoryArticles = getArticlesByCategory(activeSlug);

  if (!currentCategory) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h1 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 华丽的外出`,
          description: currentCategory.description,
          url: `https://www.hualidewaichu.com/category/${activeSlug}`,
          inLanguage: "zh-CN",
          isPartOf: {
            "@type": "WebSite",
            name: "华丽的外出",
            url: "https://www.hualidewaichu.com",
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.hualidewaichu.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: currentCategory.name,
            },
          ],
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: t("breadcrumbHome"), href: "/" },
                { label: currentCategory.name },
              ]}
            />

            <div className="mb-10 md:mb-14">
              <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-3">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 max-w-2xl">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:bg-background-100/80"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <h2 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <span>{article.readingTime} 分钟阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">{t("noArticles")}</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}